// Bulk "deactivate all non-core members" — the write half of the admin danger
// zone's DzDeactivateAll action (pages/admin/danger.js).
//
// EXTRACTED FROM THE COMPONENT ON PURPOSE. This loop decides, per member,
// WHICH user id gets written. When that decision lived inside a `_dangerRow`
// closure it could not be asserted by anything, and it was wrong for months in
// a way that returned HTTP 200 the whole time (see below). It lives here so
// __tests__/deactivateNonCoreMembers.test.js can pin the targets.
//
// ── THE BUG THIS REPLACES ────────────────────────────────────────────────
// The old body called:
//
//   sdk.execute('workspaceProject.userProfiles', 'update',
//               { id: p.userId, payload: { status: 'left' } })
//
// which routed to the `/sb` PostgREST passthrough. That proxy lists
// `user_profiles` in USER_SCOPED_TABLES (server/workers/workspace-project/
// src/surfaces/sb.js) and therefore injects `user_id=eq.<caller>` into the URL
// filter AND overwrites `row.user_id` in the body from the verified auth
// scope — it must, because it forwards with service_role and has to re-impose
// the scoping RLS would otherwise do. The `id` the caller passed was accepted
// and then discarded.
//
// Net effect, every iteration: the ACTING ADMIN's own profile row was stamped
// `status: 'left'`, once per member. No member was ever deactivated (the
// feature silently did nothing) and the admin repeatedly marked themselves as
// having left. Both failures returned 200, which is why a smoke test never
// caught it — asserting a successful status here proves nothing.
//
// ── THE FIX ─────────────────────────────────────────────────────────────
// `sdk.updateUserHrProfile(userId, patch)` addresses
// `PATCH /core/users/:userId/hr-profile` (server UserHrProfileService), which
// resolves the target across all three id spaces the people roster emits
// (Mongo _id | supabaseUserId UUID | email), permits a self-write, requires a
// platform admin to write anyone else, and returns 403 rather than
// mis-scoping. A caller without the rights now FAILS LOUDLY instead of
// corrupting its own row behind a success status.

// Roles that keep a member active. Anyone whose effective role is outside this
// set is "non-core" and gets deactivated.
const CORE_ROLES = new Set(['superadmin', 'admin', 'manager', 'member'])

// Resolve a person's effective role. CU-10 §90 moved roles from `p.roles` onto
// `root.roles.byUser`; the legacy per-person array is still honored when
// present so a stale roster shape doesn't silently reclassify everyone.
const _isCore = (person, rolesByUser) => {
  const legacyRoles = Array.isArray(person.roles) && person.roles.length > 0 ? person.roles : null
  if (legacyRoles) return legacyRoles.some((r) => CORE_ROLES.has(r))
  return CORE_ROLES.has(rolesByUser[person.userId] || 'member')
}

// Which members the action would write, in roster order. Pure — no SDK, no
// state — so the selection rule can be asserted independently of transport.
// Skips anyone already `left` (idempotent re-runs write nothing) and anyone
// without a resolvable userId (an unidentified row must never fall through to
// a caller-scoped write).
export const selectNonCoreMembers = (people, rolesByUser = {}) =>
  (Array.isArray(people) ? people : []).filter(
    (p) => p && p.userId && p.status !== 'left' && !_isCore(p, rolesByUser)
  )

/**
 * Deactivate every non-core member.
 *
 * @param {object} sdk    SDK instance (getSDK()); needs `updateUserHrProfile`.
 * @param {object} source `{ people, rolesByUser }` off the workspace root.
 * @returns {Promise<{ targeted: string[], failed: Array<{ userId, error }> }>}
 *   `targeted` is the ids actually written, in order — the value the test
 *   asserts against. A failure does not abort the sweep: one member's 403 or
 *   404 must not leave the remainder untouched, so errors are collected and
 *   rethrown as an aggregate once every member has been attempted.
 */
export const deactivateNonCoreMembers = async (sdk, { people, rolesByUser } = {}) => {
  if (!sdk || typeof sdk.updateUserHrProfile !== 'function') {
    throw new Error(
      '[deactivateNonCoreMembers] sdk.updateUserHrProfile is unavailable — refusing to fall back to the caller-scoped userProfiles route'
    )
  }

  const targets = selectNonCoreMembers(people, rolesByUser)
  const targeted = []
  const failed = []

  for (const person of targets) {
    try {
      await sdk.updateUserHrProfile(person.userId, { status: 'left' })
      targeted.push(person.userId)
    } catch (error) {
      failed.push({ userId: person.userId, error })
    }
  }

  if (failed.length) {
    const err = new Error(
      `Deactivated ${targeted.length} of ${targets.length} members — ${failed.length} failed (${
        failed[0].error?.message || 'unknown error'
      })`
    )
    err.targeted = targeted
    err.failed = failed
    throw err
  }

  return { targeted, failed }
}

export default deactivateNonCoreMembers
